import { useCallback, useEffect, useState } from 'react';
import { api, type Asset } from '../../lib/api';
import { GAVE_UP, GIVE_UP_AFTER, LOOK_EVERY_MS } from '../../lib/waiting';

// ---------------------------------------------------------------------------
// The parts every Quick tab is built from.
//
// One stage, one send button, one quote line, one "Open in Pro", and one
// watch on the library. Shared so the six tabs say the price the same way
// and wait the same way, and so a test can pin the promises once: the price
// is on the button, the quote line is never blank, and a press is followed
// by a poll rather than a screen that looks finished and is not. The model
// picker and its option chips are components/ModelPicker.tsx and
// components/ModelOptions.tsx (204), shared with the Pro benches.
// ---------------------------------------------------------------------------

/** 16:9 on the darkest ground. Holds its shape while empty, so the page does not jump when the picture lands. */
export function Stage({ children, empty, tool }: { children?: React.ReactNode; empty?: string; tool?: string }) {
  return (
    <div className="quick-stage" data-tool={tool ?? 'quick-stage'}>
      {children ?? <p className="quick-stage-empty">{empty ?? 'What you make lands here.'}</p>}
    </div>
  );
}

/**
 * The press, with the price on it.
 *
 * Disabled with the reason in `title`, never silently: a grey button with no
 * explanation is the thing that makes a creator click it four times. The
 * price is what the tier says before the press; the bill is what the server
 * meters after it.
 */
export function SendButton({ label, busyLabel, busy, credits, why, onClick }: {
  label: string; busyLabel?: string; busy: boolean;
  /** Null while the price list is still loading. */
  credits: number | null;
  why: string | null; onClick: () => void;
}) {
  const off = busy || Boolean(why) || credits === null;
  return (
    <button type="button" className="btn pri quick-send" data-move="quick-send" disabled={off}
            title={why ?? (credits === null ? 'Reading the price list…' : `${credits.toLocaleString()} credits`)}
            onClick={onClick}>
      {busy ? (busyLabel ?? 'Working…') : label}
      <span className="quick-send-price">{credits === null ? '…' : `${credits.toLocaleString()} cr`}</span>
    </button>
  );
}

/** The price in a sentence, under the controls. Always rendered; a blank quote reads as "free". */
export function QuoteLine({ text }: { text: string | null | undefined }) {
  return <p className="quick-quote" data-tool="quick-quote">{text?.trim() || 'Pricing…'}</p>;
}

/** The door to the bench that owns this work, carrying the form with it. */
export function OpenInPro({ onClick, what }: { onClick: () => void; what?: string }) {
  return (
    <button type="button" className="btn tiny" data-tool="quick-open-pro" onClick={onClick}
            title={what ?? 'The same bench with every control on it. What you have typed comes with you.'}>
      Open in Pro
    </button>
  );
}

/**
 * Watch one asset until the render plane has looked at it.
 *
 * A made picture arrives promoting, like an upload, and is usable a minute
 * or two later. The Assets bench used to stop at the press, and the picture
 * only ever appeared on a reload. This polls the library on the shared
 * cadence and gives up out loud after the shared bound, with a "Look again".
 */
export function useWatchAsset(id: string | null, brandId: string | null): {
  asset: Asset | null; stalled: boolean; lookAgain: () => void;
} {
  const [asset, setAsset] = useState<Asset | null>(null);
  const [stalled, setStalled] = useState(false);
  const [round, setRound] = useState(0);

  useEffect(() => {
    setAsset(null); setStalled(false);
    if (!id) return;
    let live = true; let looks = 0;
    const look = async () => {
      try {
        const all = await api.assets(brandId);
        const a = all.find((x) => x.id === id) ?? null;
        if (!live) return;
        if (a) setAsset(a);
        if (a && (a.status === 'ready' || a.status === 'error')) { clearInterval(t); return; }
        if (looks++ >= GIVE_UP_AFTER) { clearInterval(t); setStalled(true); }
      } catch { /* the next look tries again */ }
    };
    const t = setInterval(() => { void look(); }, LOOK_EVERY_MS);
    void look();
    return () => { live = false; clearInterval(t); };
  }, [id, brandId, round]);

  const lookAgain = useCallback(() => setRound((n) => n + 1), []);
  return { asset, stalled, lookAgain };
}

/** What the watch says while it waits, and when it has run out. */
export function WatchNote({ asset, stalled, lookAgain, waiting }: { asset: Asset | null; stalled: boolean; lookAgain: () => void; waiting: string }) {
  if (stalled) return <p className="sub-dim quick-note">{GAVE_UP} <button type="button" className="btn tiny" onClick={lookAgain}>Look again</button></p>;
  if (asset?.status === 'error') return <p className="err inline quick-note" role="alert">{asset.error ?? 'The render plane could not use it.'}</p>;
  if (asset && asset.status !== 'ready') return <p className="sub-dim quick-note">{waiting}</p>;
  return null;
}

/** A full-size signed URL for a ready asset, or null until it is one. */
export function useAssetUrl(asset: Asset | null): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    setUrl(null);
    if (!asset || asset.status !== 'ready') return;
    let live = true;
    api.assetUrl(asset.id).then((u) => { if (live) setUrl(u.url); }).catch(() => undefined);
    return () => { live = false; };
  }, [asset?.id, asset?.status]);
  return url;
}
